import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { X, Music } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

const AuthPromptContext = createContext();

export const useAuthPrompt = () => useContext(AuthPromptContext);

const DEFAULT_TITLE = 'Join AUDIVUE';
const DEFAULT_BODY = 'Sign up to get the full experience.';
const DEFAULT_PRIMARY = 'Sign Up Free';
const DEFAULT_SECONDARY = 'Already have an account? Log In';

// Two variants of the like-auth-prompt copy. Variant A = control (current).
// Variant B = "save this song" framing — hypothesis: song-language converts
// better than video-language because anon users came in for music, not video.
// Sticky-per-session assignment via localStorage so the same anon user sees
// the same variant across every surface they encounter.
const LIKE_PROMPT_VARIANTS = {
  a: {
    title: 'Sign in to like this video',
    body: 'Your likes save to your library and help tune your feed.',
    primaryButton: 'Sign in',
    secondaryButton: 'Create an account',
    primaryPath: '/login',
  },
  b: {
    title: 'Sign in to save this song',
    body: "Save this to your library and we'll tune your feed.",
    primaryButton: 'Sign in',
    secondaryButton: 'Create an account',
    primaryPath: '/login',
  },
};

const VARIANT_STORAGE_KEY = 'audivue_like_ab_variant';
const SESSION_STORAGE_KEY = 'audivue_anon_session_id';

const getOrAssignVariant = () => {
  try {
    const existing = localStorage.getItem(VARIANT_STORAGE_KEY);
    if (existing === 'a' || existing === 'b') return existing;
    const assigned = Math.random() < 0.5 ? 'a' : 'b';
    localStorage.setItem(VARIANT_STORAGE_KEY, assigned);
    return assigned;
  } catch {
    // Private mode / storage disabled — fall back to per-call random so we
    // still serve a variant, just without stickiness.
    return Math.random() < 0.5 ? 'a' : 'b';
  }
};

const getOrAssignSessionId = () => {
  try {
    const existing = localStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) return existing;
    const sid = `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem(SESSION_STORAGE_KEY, sid);
    return sid;
  } catch {
    return `s-${Date.now()}`;
  }
};

// Fire-and-forget event log. Failures are swallowed silently — we don't want
// a flaky analytics call to break the like prompt.
const logAbEvent = (variant, event, videoId) => {
  try {
    axios.post(`${API}/api/ab/like-prompt/event`, {
      variant, event,
      video_id: videoId || null,
      session_id: getOrAssignSessionId(),
    }).catch(() => { /* best-effort, non-blocking */ });
  } catch { /* ignore */ }
};

export const AuthPromptProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [opts, setOpts] = useState({
    title: DEFAULT_TITLE,
    body: DEFAULT_BODY,
    primaryButton: DEFAULT_PRIMARY,
    secondaryButton: DEFAULT_SECONDARY,
    primaryPath: '/register',
    abVariant: null,
    abVideoId: null,
  });

  // Accepts either:
  //   promptAuth('legacy message string')                — back-compat
  //   promptAuth({ title, body, primaryButton, secondaryButton, primaryPath,
  //                abVariant, abVideoId })
  // Used for context-specific copy (e.g. "Sign in to like this video").
  const promptAuth = useCallback((msgOrOpts = DEFAULT_BODY) => {
    if (typeof msgOrOpts === 'string') {
      setOpts({
        title: DEFAULT_TITLE,
        body: msgOrOpts,
        primaryButton: DEFAULT_PRIMARY,
        secondaryButton: DEFAULT_SECONDARY,
        primaryPath: '/register',
        abVariant: null,
        abVideoId: null,
      });
    } else {
      setOpts({
        title: msgOrOpts.title || DEFAULT_TITLE,
        body: msgOrOpts.body || DEFAULT_BODY,
        primaryButton: msgOrOpts.primaryButton || DEFAULT_PRIMARY,
        secondaryButton: msgOrOpts.secondaryButton || DEFAULT_SECONDARY,
        primaryPath: msgOrOpts.primaryPath || '/register',
        abVariant: msgOrOpts.abVariant || null,
        abVideoId: msgOrOpts.abVideoId || null,
      });
    }
    setIsOpen(true);
  }, []);

  const close = useCallback(() => setIsOpen(false), []);

  return (
    <AuthPromptContext.Provider value={{ promptAuth }}>
      {children}
      {isOpen && <AuthPromptModal {...opts} onClose={close} />}
    </AuthPromptContext.Provider>
  );
};

const AuthPromptModal = ({ title, body, primaryButton, secondaryButton, primaryPath, abVariant, abVideoId, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  // Guard so a frantic double-click on Sign in doesn't double-log the CTA
  // (the modal closes synchronously after click, but useEffect cleanup races
  // could still fire twice without this).
  const ctaLoggedRef = useRef(false);

  // Encode the current URL so the auth pages can send the user back here
  // after a successful login/signup. Critical "don't lose context" guard —
  // without it, signing up from a like button would dump the user on /home
  // instead of returning them to the video they were trying to like.
  const nextParam = encodeURIComponent(location.pathname + location.search);

  const go = (path, isPrimary = false) => {
    // Log the CTA click ONLY when this came from the primary button.
    // The previous `path === primaryPath` heuristic was unreliable because
    // both primary and secondary often resolve to the same destination
    // (e.g. '/login') for the like variants — so we now pass an explicit
    // boolean from each button rather than infer.
    if (abVariant && isPrimary && !ctaLoggedRef.current) {
      ctaLoggedRef.current = true;
      logAbEvent(abVariant, 'cta_click', abVideoId);
    }
    onClose();
    navigate(`${path}?next=${nextParam}`);
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" onClick={onClose} data-testid="auth-prompt-modal">
      <div className="absolute inset-0 bg-black/70" />
      <div
        className="relative w-full max-w-sm bg-zinc-900 rounded-2xl overflow-hidden animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={onClose} className="absolute top-3 right-3 text-zinc-500 hover:text-white z-10">
          <X className="w-5 h-5" />
        </button>

        <div className="px-6 pt-8 pb-6 text-center">
          <div className="w-14 h-14 rounded-full bg-orange-500/10 flex items-center justify-center mx-auto mb-4">
            <Music className="w-7 h-7 text-orange-500" />
          </div>
          <h3 className="text-xl font-bold text-white mb-2" data-testid="auth-prompt-title">{title}</h3>
          <p className="text-sm text-zinc-400 leading-relaxed" data-testid="auth-prompt-body">{body}</p>
        </div>

        <div className="px-6 pb-6 space-y-3">
          <Button
            onClick={() => go(primaryPath, true)}
            className="w-full bg-white hover:bg-zinc-200 text-black font-semibold py-3 rounded-full"
            data-testid="auth-prompt-signup"
          >
            {primaryButton}
          </Button>
          <Button
            onClick={() => go('/login', false)}
            variant="outline"
            className="w-full border-zinc-700 text-white hover:bg-zinc-800 py-3 rounded-full"
            data-testid="auth-prompt-login"
          >
            {secondaryButton}
          </Button>
        </div>
      </div>
    </div>
  );
};

/**
 * Shared "must be signed in to like" gate — used by every like control on
 * every surface (video cards, mini-player, full player, Pulse, video detail
 * page). Centralised so the copy and behaviour are identical app-wide and
 * we can't accidentally let phantom likes through that look real but vanish.
 *
 * As of iter 79 (Feb 2026) this hook ALSO drives the like-prompt A/B test:
 *   - Each anon visitor gets a sticky variant (a or b) in localStorage.
 *   - We log "shown" the moment the modal opens, "cta_click" when they tap
 *     Sign in. Admin reads /api/admin/ab/like-prompt for the funnel.
 *   - Variant copy lives in LIKE_PROMPT_VARIANTS at the top of this file —
 *     swap copy / add variants there, no caller changes needed.
 */
export const useLikeAuthGate = () => {
  const { promptAuth } = useAuthPrompt();
  return useCallback((user, videoId = null) => {
    if (user) return true;
    const variant = getOrAssignVariant();
    const copy = LIKE_PROMPT_VARIANTS[variant];
    promptAuth({ ...copy, abVariant: variant, abVideoId: videoId });
    // Log the impression after dispatching the modal (microtask gap) so we
    // never block the UI. Failures are silently swallowed inside logAbEvent.
    logAbEvent(variant, 'shown', videoId);
    return false;
  }, [promptAuth]);
};

// Legacy export kept so any caller still importing LIKE_AUTH_PROMPT directly
// (instead of going through useLikeAuthGate) gets the control copy. Both new
// and old code paths agree on the control text.
export const LIKE_AUTH_PROMPT = LIKE_PROMPT_VARIANTS.a;
